import React, { FC, useEffect, useRef, useState } from 'react';
import { EditorHeader } from '../sharedComponents';
import EditorTitle from '../commonElements/editorTitle';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import {
  createTextBlockFolder,
  createTextCaption,
  getTextBlockCaptions,
  getTextBlocksFolder
} from '../../../../redux/slices/textBlocks';
import Folders from './components/folders';
import TextBlocks from './components/textBlocks';
import CaptionModals from './modals/captionModals';
import CaptionModal from './modals/captionModals';
import { lomavisShowToastr } from '../../../../utils/ui/toastr';

interface Props {
  currentPlatform: string;
  handleClose: () => void;
}
const ContentTextBlocks: FC<Props> = ({ currentPlatform, handleClose }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();
  const footerRef = useRef(null);

  const [rightMargin, setRightMargin] = useState(0);
  const [currentFocusFolder, setCurrentFocusFolder] = useState('');
  const [nullFolder, setNullFolder] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const lomavisCreator = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );
  const { cloudUuid, locationUuid } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );

  const { allTextBlockFolders } = useSelector((state: any) => state.textBlocks);

  useEffect(() => {
    dispatch(
      getTextBlocksFolder({
        locationUUID: locationUuid,
        cloudUUID: cloudUuid,
        onError: (err) => {
          lomavisShowToastr(
            t('lomavisPostApproval.textBlock.err_gettingBlockFolder'),
            'error'
          );
        }
      })
    );
  }, []);
  useEffect(() => {
    dispatch(
      getTextBlockCaptions({
        locationUUID: locationUuid,
        cloudUUID: cloudUuid,
        folderUUID: currentFocusFolder,
        folderIsNull: nullFolder,
        onError: (err) => {
          lomavisShowToastr(
            t('lomavisPostApproval.textBlock.err_gettingTextBlock'),
            'error'
          );
        }
      })
    );
  }, [currentFocusFolder, nullFolder]);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 800); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Trigger actual search
  useEffect(() => {
    if (debouncedValue) {
      if (debouncedValue.length >= 2) {
        setIsSearching(true);
        dispatch(
          getTextBlockCaptions({
            locationUUID: locationUuid,
            cloudUUID: cloudUuid,
            search: debouncedValue,
            onError: (err) => {
              setIsSearching(false);

              lomavisShowToastr(
                t('lomavisPostApproval.textBlock.err_gettingTextBlock'),
                'error'
              );
            },
            onSuccess: () => {
              setIsSearching(false);
            }
          })
        );
      }
    } else {
      dispatch(
        getTextBlockCaptions({
          locationUUID: locationUuid,
          cloudUUID: cloudUuid,
          onError: (err) => {
            lomavisShowToastr(
              t('lomavisPostApproval.textBlock.err_gettingTextBlock'),
              'error'
            );
          }
        })
      );
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (debouncedValue && currentFocusFolder) {
      setCurrentFocusFolder('');
    }
  }, [searchTerm]);
  useEffect(() => {
    if (debouncedValue && currentFocusFolder) {
      setSearchTerm('');
    }
  }, [currentFocusFolder]);

  useEffect(() => {
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  return (
    <div className="w-100 h-100 d-flex flex-column gap-4 justify-content-between">
      <EditorHeader>
        <div
          style={{}}
          className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor"
        >
          <div className="h-100 flex-fill d-flex justify-content-start align-items-center gap-3">
            <EditorTitle title={'captions'} toolTip={true} />
          </div>
          <div
            style={{ flex: 1 }}
            className="d-flex flex-fill justify-content-end align-item-center gap-3"
          >
            <Button
              className="d-flex align-items-center fs-4"
              size="sm"
              variant="primary"
              onClick={() => {
                setShowCaptionModal(true);
              }}
            >
              <i className="fa-duotone fa-solid fa-plus"></i>{' '}
              {t('lomavisCreator.textBlock.create')}
            </Button>

            {showCaptionModal && (
              <CaptionModal
                show={showCaptionModal}
                onClose={() => setShowCaptionModal(false)}
                onCreate={({ title, text_as_html, text_quill }) => {
                  dispatch(
                    createTextCaption(
                      locationUuid,
                      cloudUuid,
                      {
                        title: title,
                        text_as_html: text_as_html,
                        text_quill: text_quill,
                        location: locationUuid
                      },
                      () => {
                        lomavisShowToastr(
                          t(
                            'lomavisPostApproval.textBlock.success_captionCreated'
                          ),
                          'success'
                        );
                      },

                      () => {
                        lomavisShowToastr(
                          t('lomavisPostApproval.textBlock.err_captionCreated'),
                          'error'
                        );
                      },

                      currentFocusFolder
                    )
                  );
                }}
                action="create"
              />
            )}
          </div>
        </div>
      </EditorHeader>

      <div
        className="w-100 flex-fill d-flex gap-4"
        style={{ overflowY: 'hidden' }}
      >
        <aside
          className="rounded-3 bg-white py-2 px-3 flex-fill "
          style={{
            maxWidth: '500px',
            width: '25%',
            minWidth: '300px'
          }}
        >
          <Folders
            currentFocusFolder={currentFocusFolder}
            setCurrentFocusFolder={setCurrentFocusFolder}
            nullFolder={nullFolder}
            setNullFolder={setNullFolder}
          />
        </aside>

        <div className="rounded-3 bg-white py-2 px-3 h-100 w-100 d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-start pt-2">
            <EditorTitle title={'textBlocks'} toolTip={false} />{' '}
            <div className="d-flex flex-column gap-1 justify-content-end align-items-end w-50">
              {' '}
              <div
                className="position-relative"
                style={{ maxWidth: '400px', width: '100%' }}
              >
                <Form.Control
                  style={{ width: '100%' }}
                  placeholder={t('lomavisCreator.textBlock.Search')}
                  aria-label="homepage_input"
                  aria-describedby="basic-addon1"
                  id="homepage_input"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (e.target.value) {
                      setIsSearching(true);
                    }
                  }}
                  autoComplete="off"
                />
                {isSearching && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-25px',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end">
                <Button
                  variant="light"
                  className="p-1 px-2 m-0 text-decoration-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    display: searchTerm ? 'block' : 'none'
                  }}
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentFocusFolder('');
                  }}
                >
                  {t('lomavisCreator.textBlock.clear')}
                </Button>
              </div>
            </div>
          </div>
          <div className="py-2 px-3 flex-fill" style={{ overflowY: 'hidden' }}>
            <TextBlocks
              createCaption={() => {
                setShowCaptionModal(true);
              }}
            />
          </div>
        </div>
      </div>

      <div
        ref={footerRef}
        style={{
          bottom: '0',
          zIndex: 100,
          height: '60px'
        }}
        className="w-100 d-flex justify-content-end gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
      >
        <Button
          onClick={() => {
            handleClose();
          }}
          style={{ marginRight: rightMargin + 'px' }}
          variant="secondary"
          size="sm"
        >
          {t('lomavisCreator.cancel')}
        </Button>
      </div>
    </div>
  );
};

export default ContentTextBlocks;
